// src/domains/core/dashboard/components/widgets/base/WidgetContainer.tsx

import type { WidgetContainerProperties } from "../../../types/widget.types";

import { AlertTriangle } from "lucide-react";
import React, { type FC } from "react";

import { Card } from "@/components/ui";
import { cn } from "@/shared/utils";

import { WidgetHeader } from "./WidgetHeader";
import { WidgetLoader } from "./WidgetLoader";

/**
 * WidgetContainer - Basis-Container für alle Dashboard-Widgets
 *
 * Stellt einheitliches Styling, Loading States und Error Handling bereit
 *
 * @param props - Component props
 *
 * @param props.widgetId - Widget identifier
 *
 * @param props.title - Widget title
 *
 * @param props.description - Widget description
 *
 * @param props.icon - Widget icon
 *
 * @param props.loading - Loading state
 *
 * @param props.error - Error message
 *
 * @param props.onRefresh - Refresh callback
 *
 * @param props.onGenerate - Generate callback
 *
 * @param props.isGenerating - Generating state
 *
 * @param props.onSettings - Settings callback
 *
 * @param props.className - Additional CSS classes
 *
 * @param props.children - Widget content
 *
 * @param props.size - Widget size
 *
 * @returns Widget container component
 *
 */
export const WidgetContainer: FC<WidgetContainerProperties> = ({
  children,
  className,
  description,
  error = null,
  icon,
  isGenerating = false,
  loading = false,
  onGenerate,
  onRefresh,
  onSettings,
  size = "medium",
  title,
  widgetId: _widgetId,
}: WidgetContainerProperties) => {
  // NOTE: widgetId is consumed by parent registry/config; kept for consistency
  // Reason: widgetId is intentionally unused but required by interface
  const _unusedWidgetId = _widgetId;
  // Reason: widgetId is intentionally unused but required by interface, void operator marks it as intentionally ignored
  // eslint-disable-next-line sonarjs/void-use -- widgetId is intentionally unused but required by interface
  void _unusedWidgetId;

  // Size-based height classes
  type WidgetSizeType = "large" | "medium" | "small" | "xlarge";
  const heightMap: Record<WidgetSizeType, string> = {
    large: "min-h-[16rem]",
    medium: "min-h-[12rem]",
    small: "min-h-[8rem]",
    xlarge: "min-h-[20rem]",
  };

  // Loading State
  if (loading) {
    const loaderSize: WidgetSizeType = size as WidgetSizeType;
    return <WidgetLoader {...(className ? { className } : {})} size={loaderSize} />;
  }

  // Error State
  if (error) {
    const sizeKey: WidgetSizeType = size as WidgetSizeType;
    const heightClass = heightMap[sizeKey];
    return (
      <Card className={cn(heightClass, className)} padding="md" variant="default">
        <WidgetHeader
          {...(description ? { description } : {})}
          icon={icon}
          isGenerating={isGenerating}
          {...(onGenerate ? { onGenerate } : {})}
          {...(onRefresh ? { onRefresh } : {})}
          {...(title ? { title } : {})}
        />
        <div className="flex flex-col items-center justify-center h-full text-center py-8">
          <AlertTriangle className="w-8 h-8 text-red-400 mb-2" />
          <p className="text-sm text-gray-400 mb-1">Error loading widget</p>
          <p className="text-xs text-gray-500">{error}</p>
          {onRefresh ? (
            <button
              className="mt-4 text-xs text-tracq-bright-blue hover:text-tracq-deep-blue transition-colors"
              onClick={onRefresh}
            >
              Try again
            </button>
          ) : null}
        </div>
      </Card>
    );
  }

  // Normal State
  const sizeKey: WidgetSizeType = size as WidgetSizeType;
  const heightClass = heightMap[sizeKey];
  return (
    <Card
      className={cn(
        "flex flex-col",
        heightClass,
        "transition-all duration-200",
        "hover:shadow-lg",
        className,
      )}
      padding="md"
      variant="default"
    >
      {(() => {
        if (!title && !icon) {
          return null;
        }
        const headerProps: {
          description?: string;
          icon?: React.ReactNode;
          isGenerating?: boolean;
          onGenerate?: () => void;
          onRefresh?: () => void;
          onSettings?: () => void;
          title?: string;
        } = {
          icon,
          isGenerating,
        };
        if (description) {
          headerProps.description = description;
        }
        if (onGenerate) {
          headerProps.onGenerate = onGenerate;
        }
        if (onRefresh) {
          headerProps.onRefresh = onRefresh;
        }
        if (onSettings) {
          headerProps.onSettings = onSettings;
        }
        if (title) {
          headerProps.title = title;
        }
        return <WidgetHeader {...headerProps} />;
      })()}

      <div className="flex-1 flex flex-col">{children}</div>
    </Card>
  );
};
